/**
 * Pure helpers for ExecutionsByStatusPage — no React, no JSX.
 *
 * Suggestions + property catalogue + relative date options are read
 * by the page's UI controls; validate + buildRequest are called from
 * the page component and slice thunks. All state lives in the
 * executionsByStatusSlice.
 */
import { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import {
  BaseFormState,
  GetExecutionsByStatusRequest,
} from 'src/store/slices/executionsByStatus/executionsByStatusSlice';

// TODO(FLMB): Fetch suggestion lists from the backend once an endpoint is
// exposed — the allowed values for status/state differ by dataSource, so a
// static list is a stopgap that will drift out of date. Same applies to
// DATA_SOURCE_SUGGESTIONS which currently mirrors CLIENT_DATA_SOURCE_MAP in
// FintechLastMileBeaconCDK/lib/config/client-config.ts.
export const STATUS_SUGGESTIONS = [
  'SEND_TO_BANK', 'RECEIVED', 'VALIDATION', 'TRANSFORMATION', 'BANK_RESPONSE',
];
export const STATE_SUGGESTIONS = ['SUCCESS', 'FAILED', 'IN_PROGRESS', 'PENDING', 'CANCELLED'];
export const DATA_SOURCE_SUGGESTIONS = ['BIP_V1', 'BIP_V2', 'UAPI', 'LMPS'];
export const DEFAULT_PAGE_SIZE = 50;

/**
 * PropertyFilter FilteringProperty catalogue. All keys are exact-match
 * execution fields passed through as additionalFilters to the API. Date
 * range lives in the sibling DateRangePicker.
 */
export const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'executionType', propertyLabel: 'Execution type', groupValuesLabel: 'Execution type values', operators: ['='] },
  { key: 'pluginName', propertyLabel: 'Plugin name', groupValuesLabel: 'Plugin name values', operators: ['='] },
  { key: 'paymentType', propertyLabel: 'Payment type', groupValuesLabel: 'Payment type values', operators: ['='] },
  { key: 'parentWorkflowId', propertyLabel: 'Parent workflow ID', groupValuesLabel: 'Parent workflow ID values', operators: ['='] },
];

export const RELATIVE_DATE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
  { key: 'previous-1-hour', amount: 1, unit: 'hour', type: 'relative' },
  { key: 'previous-6-hours', amount: 6, unit: 'hour', type: 'relative' },
  { key: 'previous-24-hours', amount: 24, unit: 'hour', type: 'relative' },
  { key: 'previous-3-days', amount: 3, unit: 'day', type: 'relative' },
  { key: 'previous-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'previous-30-days', amount: 30, unit: 'day', type: 'relative' },
];

const parseYmd = (iso: string) => iso.split('-').map((s) => parseInt(s, 10));

/** Local midnight (00:00:00.000) for yyyy-mm-dd → epoch ms. */
const isoDateToStartOfDayMs = (iso: string) => {
  const [y, m, d] = parseYmd(iso);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
};

/** Local end-of-day (23:59:59.999) for yyyy-mm-dd → epoch ms. */
const isoDateToEndOfDayMs = (iso: string) => {
  const [y, m, d] = parseYmd(iso);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
};

/** Reduces PropertyFilter tokens to additionalFilters map. */
export const additionalFiltersFromQuery = (query: PropertyFilterProps.Query): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const t of query.tokens) {
    if (typeof t.propertyKey !== 'string' || typeof t.value !== 'string' || t.operator !== '=' || !t.value.trim()) continue;
    out[t.propertyKey] = t.value.trim();
  }
  return out;
};

const UNIT_MS: Record<string, number> = {
  second: 1_000, minute: 60_000, hour: 3_600_000, day: 86_400_000,
  week: 604_800_000, month: 2_592_000_000, year: 31_536_000_000,
};

/**
 * Normalizes a DateRangePicker value to inclusive [fromMs, toMs] epoch
 * ms bounds. Absolute yyyy-mm-dd values map to local midnight → end-of-
 * day; relative values snapshot against Date.now() at call time.
 * Returns null when the range is missing or unparseable.
 */
export const dateRangeToEpochMs = (
  value: DateRangePickerProps.Value | null,
): { fromMs: number; toMs: number } | null => {
  if (!value) return null;
  if (value.type === 'absolute') {
    if (!value.startDate || !value.endDate) return null;
    const fromMs = value.startDate.includes('T')
      ? new Date(value.startDate).getTime()
      : isoDateToStartOfDayMs(value.startDate);
    const toMs = value.endDate.includes('T')
      ? new Date(value.endDate).getTime()
      : isoDateToEndOfDayMs(value.endDate);
    return Number.isNaN(fromMs) || Number.isNaN(toMs) ? null : { fromMs, toMs };
  }
  const scale = UNIT_MS[value.unit];
  if (!scale || !Number.isFinite(value.amount)) return null;
  const now = Date.now();
  return { fromMs: now - value.amount * scale, toMs: now };
};

export interface FormValidation {
  ok: boolean;
  errors: { status?: string; state?: string; dataSource?: string; dateRange?: string };
}

export const validate = (
  form: BaseFormState,
  dateRange: DateRangePickerProps.Value | null,
): FormValidation => {
  const errors: FormValidation['errors'] = {};
  if (!form.status.trim()) errors.status = 'Status is required.';
  if (!form.state.trim()) errors.state = 'State is required.';
  if (!form.dataSource.trim()) errors.dataSource = 'Data source is required.';
  const ms = dateRangeToEpochMs(dateRange);
  if (ms === null) errors.dateRange = 'Select an Updated at range.';
  else if (ms.fromMs > ms.toMs) errors.dateRange = 'Range start must be on or before range end.';
  return { ok: Object.keys(errors).length === 0, errors };
};

/**
 * Builds the API request. Callers must validate() first — this trusts
 * inputs. The `!` on dateRangeToEpochMs is safe because validation
 * blocks submission when the range is null.
 */
export const buildRequest = (
  form: BaseFormState,
  dateRange: DateRangePickerProps.Value | null,
  query: PropertyFilterProps.Query,
  pageNumber: number,
  pageSize: number,
): GetExecutionsByStatusRequest => {
  const additionalFilters = additionalFiltersFromQuery(query);
  const { fromMs, toMs } = dateRangeToEpochMs(dateRange)!;
  return {
    executionQuery: {
      status: form.status.trim(),
      state: form.state.trim(),
      dataSource: form.dataSource.trim(),
      ...(Object.keys(additionalFilters).length > 0 ? { additionalFilters } : {}),
    },
    fromDate: fromMs,
    toDate: toMs,
    sortBy: form.sortBy,
    pagination: { pageNumber, pageSize },
  };
};
